import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { Lock, User } from 'lucide-react';
import { useNotification } from '../context/NotificationContext';

const Login = () => {
  const { notify } = useNotification();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // --- STEP 1: Request Login ---
      const response = await api.post('auth/login/', formData);
      console.log("LOGIN RAW RESPONSE:", response.data); 

      let token = null;
      let role = null;
      let username = formData.username;

      // --- DIAGNOSTIC FIX: Handle HTML Errors ---
      if (typeof response.data === 'string') {
        if (response.data.trim().startsWith('<')) {
            // Extract the <title> tag to see what went wrong (e.g. "404 Not Found")
            const titleMatch = response.data.match(/<title>(.*?)<\/title>/i);
            const pageTitle = titleMatch ? titleMatch[1] : 'Unknown HTML Page';
            
            // THROW SPECIFIC ERROR
            throw new Error(`Server Error: Endpoint not found or crashed. Server sent HTML page: "${pageTitle}"`);
        }
        // If it's a string but not HTML, assume it's the raw token
        token = response.data;
      } 
      else if (typeof response.data === 'object') {
        token = response.data.token || response.data.access || response.data.key;
        role = response.data.role || response.data.user_type;
        if (response.data.username) username = response.data.username;
      }

      // Validation
      if (!token) {
        throw new Error('Server did not return a valid token key.');
      }

      // Save token
      localStorage.setItem('token', token);

      // --- STEP 2: Fetch Role if Missing ---
      if (!role) {
        console.log("Token received, fetching User Profile...");
        try {
          const profileRes = await api.get('auth/profile/');
          role = profileRes.data.role;
          if (profileRes.data.username) username = profileRes.data.username;
        } catch (profileError) {
          console.error("Profile Fetch Error:", profileError);
          notify('warning', 'Login partial: Could not fetch user details.');
        }
      }

      // Save & Redirect
      localStorage.setItem('role', role || ''); 
      localStorage.setItem('username', username);

      notify('success', 'Login successful!');

      const roleId = Number(role);

      if (roleId === 1) {
        window.location.href = 'https://revenuesetters.com/api/admin';
      } else if (roleId === 2) {
        navigate('/company/dashboard');
        window.location.reload(); 
      } else if (roleId === 3) {
        navigate('/setter/dashboard');
        window.location.reload();
      } else {
        navigate('/dashboard'); 
        window.location.reload();
      }

    } catch (err) {
      console.error("Login Failed:", err);
      localStorage.clear(); 
      
      const errorMessage = err.message || 'Invalid username or password.';
      notify('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-light py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg border border-gray-100">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Welcome Back</h2>
          <p className="mt-2 text-sm text-gray-600">Sign in to your RevenueSetters account</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="relative">
              <User className="absolute top-3 left-3 text-gray-400" size={20} />
              <input
                type="text"
                required
                className="appearance-none rounded-t-md relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Username"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
              />
            </div>
            <div className="relative">
              <Lock className="absolute top-3 left-3 text-gray-400" size={20} />
              <input
                type="password"
                required
                className="appearance-none rounded-b-md relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${isLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-primary hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200`}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-primary hover:text-blue-500">
              Sign up free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;